import { useMemo } from 'react';
import { PageLayout, Divider, Button } from '../components';
import { MOCK_PARTICIPANTS } from '../constants';
import { useRegisterModal } from '../contexts/RegisterModalContext';
import { useLanguage } from '../contexts/LanguageContext';

function ModelCard({ model, onRegister }) {
  const { t } = useLanguage();
  return ( 
    <div className="relative w-full anim-fade-up">
      <div className="relative overflow-hidden rounded-xl xs:rounded-2xl border border-slate-200 bg-white/70 shadow-sm">
        <img
          src={model.image}
          alt=""
          className="h-[280px] xs:h-[340px] tablet:h-[420px] w-full object-cover blur-lg"
        />

        <div className="absolute inset-0 flex items-center justify-center p-4 xs:p-6">
          <div className="blur-hint w-full px-3 xs:px-5 py-3 xs:py-4 text-center">
            <p className="text-sm xs:text-base font-bold text-slate-900">
              {t('locked.hint')}
            </p>

            <Button size="md" className="mt-2 xs:mt-3 w-full text-sm xs:text-base" onClick={onRegister}>
              {t('header.register')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ModelsPage() {
  const { openRegister } = useRegisterModal();
  const { t } = useLanguage();

  const allModels = useMemo(() => {
    const base = MOCK_PARTICIPANTS.filter((p) => p.nominationId === 'models');
    const desiredCount = 9;

    if (base.length === 0) return [];
    if (base.length >= desiredCount) return base;

    const filled = [...base];
    for (let i = 0; filled.length < desiredCount; i += 1) {
      const src = base[i % base.length];
      filled.push({ ...src, id: `${src.id}-placeholder-${i + 1}` });
    }

    return filled;
  }, []);

  return (
    <PageLayout>
      <section className="flex flex-col justify-center items-center py-6 xs:py-8 tablet:py-10 px-4 xs:px-6 anim-fade-up">
        <h1 className="text-2xl xs:text-3xl 550:text-4xl tablet:text-5xl font-black tracking-tight text-center leading-tight bg-gradient-to-r from-sky-600 via-blue-600 to-sky-600 text-transparent bg-clip-text">
          {t('models.title')}
        </h1>
        <p className="mt-3 xs:mt-4 text-sm xs:text-base tablet:text-lg text-slate-600 text-center max-w-lg">
          {t('models.subtitle')}
        </p>
        <Divider gradient className="mt-4 xs:mt-6 w-full max-w-3xl" />
      </section>

      <section className="px-4 xs:px-6 pb-6 xs:pb-10 anim-fade-up" style={{ animationDelay: '80ms' }}>
        <div className="grid grid-cols-1 480:grid-cols-2 laptop:grid-cols-3 gap-3 xs:gap-4 tablet:gap-6">
          {allModels.map((model, index) => (
            <div key={model.id} style={{ animationDelay: `${120 + index * 60}ms` }}>
              <ModelCard model={model} onRegister={openRegister} />
            </div>
          ))}
        </div>

        <div className="mt-6 xs:mt-8 flex flex-col items-center gap-3 xs:gap-4 anim-fade-up" style={{ animationDelay: '260ms' }}>
          <div className="flex items-center gap-2 xs:gap-3">
            <button
              type="button"
              disabled
              className="rounded-lg xs:rounded-xl border border-slate-200 bg-white px-3 xs:px-4 py-1.5 xs:py-2 text-xs xs:text-sm font-semibold text-slate-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.back')}
            </button>

            <span className="text-xs xs:text-sm text-slate-600">{t('common.pageOf', { page: 1, total: 154 })}</span>

            <button
              type="button"
              disabled
              className="rounded-lg xs:rounded-xl border border-slate-200 bg-white px-3 xs:px-4 py-1.5 xs:py-2 text-xs xs:text-sm font-semibold text-slate-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.forward')}
            </button>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
